/**
 * Recipe Parsing Module
 * 
 * Handles parsing of AI-generated markdown into RecipeFormData structures.
 * Extracted from recipeService.ts for better modularity.
 */

import { RecipeFormData, RecipeStructured, StructuredIngredient, StructuredStep } from '../../types/recipe';

/**
 * Major section keywords that terminate content capture when extracting any section.
 * Used as a single source of truth so the description regex and the section-content
 * regex all stop at the same set of headers.
 */
const MAJOR_SECTION_KEYWORDS = [
    "Description", "Ingredients", "Method", "Instructions", "Directions",
    "Notes", "Introduction", "Serves", "Total Time", "Equipment",
    "Before You Begin", "Serving Suggestions", "Make-Ahead and Storage",
    "Variations & Substitutions", "Why This Recipe Works", "Category"
];
const MAJOR_SECTION_PATTERN = MAJOR_SECTION_KEYWORDS.join('|');

/**
 * Heuristic structural fallback for when the AI dumps everything into a single
 * description block. Detects ingredient-like lines (bullets, leading "1 cup" etc.)
 * and step-like lines ("1. Preheat...") and splits them out.
 */
function structuralFallbackParse(text: string): { description: string; ingredients: string; method: string; notes: string } {
    const lines = text.split('\n');
    const buffer: { [key: string]: string[] } = { description: [], ingredients: [], method: [], notes: [] };

    // Conservative detection: ingredients are bullets; steps are numbered with a period.
    // A numbered line WITHOUT a period is treated as an ingredient (common in pasted recipes).
    const isIngredientLine = (line: string): boolean => {
        const t = line.trim();
        if (!t) return false;
        if (/^[-*•]\s+/.test(t)) return true;
        if (/^\d+\s+\S/.test(t) && /^\d+\s+(cup|tsp|tbsp|teaspoon|tablespoon|grams?|kilograms?|g|kg|ml|l|oz|lb|ounce|pound|clove|pinch|dash|stick|can|package)s?\b/i.test(t)) return true;
        return false;
    };
    const isStepLine = (line: string): boolean => {
        const t = line.trim();
        return /^\d+[.)]\s+\S/.test(t);
    };

    let current: 'description' | 'ingredients' | 'method' | 'notes' = 'description';
    let seenIngredient = false;
    let seenMethod = false;

    for (const raw of lines) {
        const line = raw;

        if (isIngredientLine(line)) {
            if (!seenIngredient) {
                seenIngredient = true;
                current = 'ingredients';
            }
        } else if (isStepLine(line)) {
            if (!seenMethod) {
                seenMethod = true;
                current = 'method';
            }
        }

        buffer[current].push(line);
    }

    // If we never saw an ingredient/method pattern, leave them empty and only
    // try to populate notes from the tail of the description (after the first blank line)
    if (!seenIngredient && !seenMethod) {
        return {
            description: text.trim(),
            ingredients: '',
            method: '',
            notes: ''
        };
    }

    // Trim trailing empty lines from each section
    const trimTail = (arr: string[]): string => {
        while (arr.length && arr[arr.length - 1].trim() === '') arr.pop();
        return arr.join('\n').trim();
    };

    return {
        description: trimTail(buffer.description),
        ingredients: trimTail(buffer.ingredients),
        method: trimTail(buffer.method),
        notes: trimTail(buffer.notes)
    };
}

/**
 * Parses AI-generated structured markdown into RecipeFormData for form pre-filling
 * Simplified and more robust approach
 */
export function parseMarkdownToRecipeForm(
    markdown: string,
    metadata?: { persona?: string; dietaryTags?: string[] },
    rawTextFallback?: string
): Partial<RecipeFormData> {
    const formData: Partial<RecipeFormData> = {
        isFavorite: false,
        share: true,
        // Category is intentionally left undefined when the AI does not specify one.
        // The form's own default ('Asian') kicks in if the user doesn't pick anything.
        category: undefined,
        tags: [],
        notes: '',
        image_url: '',
        has_custom_image: false,
        title: '',
        description: '',
        ingredients: '',
        method: ''
    };


    try {
        // 1. Extract Title by finding the first H1 header
        const lines = markdown.split('\n');
        let titleLine = lines.find(line => line.trim().startsWith('# '));

        if (!titleLine) {
            // Fallback: look for lines that look like titles (bolded or just first significant line)
            const potentialTitles = lines.filter(line =>
                line.trim().length > 0 &&
                !line.trim().startsWith('-') &&
                !line.trim().startsWith('*') &&
                !line.trim().startsWith('>') &&
                !line.match(/^(Ingredients|Method|Instructions|Notes|Description|Introduction)/i)
            );

            if (potentialTitles.length > 0) {
                // Take the first likely candidate
                titleLine = potentialTitles[0];
            }
        }

        if (titleLine) {
            const cleanedTitle = titleLine
                .replace(/^#+\s*/, '')
                .replace(/^\*\*(.*?)\*\*$/, '$1')
                .replace(/^Title:\s*/i, '') // Handle "Title: " prefix
                .trim();

            if (!/^(Ingredients|Method|Instructions|Notes|Description)/i.test(cleanedTitle) && cleanedTitle.length > 0 && cleanedTitle.length < 100) {
                formData.title = cleanedTitle;
            } else {
                // Don't set a default - let the caller handle fallback logic
                formData.title = "";
            }
        } else {
            // Don't set a default - let the caller handle fallback logic
            formData.title = "";
        }

        // Helper to find section content, accommodating H2 or bolded headers
        // Stops at the next H2 major section header or bolded MAJOR keyword, NOT at subsections
        // This allows ingredient groups like "### For the Roasted Cauliflower:" or "**For the Pastry:**" to be captured
        const getSectionContent = (sectionKeywords: string[], markdownContent: string, _currentTitle: string): string => {
            for (const keyword of sectionKeywords) {
                // H2: ## Keyword  (also accepts ## Keyword: with trailing colon)
                // Only stops at H2 headers (##) for major sections, NOT H3 (###) subsections
                const h2Regex = new RegExp(`##\\s*${keyword}:?\\s*([\\s\\S]*?)(?=\\n##\\s*(?:${MAJOR_SECTION_PATTERN})|$)`, 'i');
                let match = markdownContent.match(h2Regex);
                if (match && match[1]) return match[1].trim();

                // Bolded format: **Keyword:** (also accepts **Keyword** without colon)
                // Only stops at MAJOR section bold headers like **Method:** or **Notes:**
                // Does NOT stop at subsection headers like **For the Pastry:** or **For the Beef:**
                const boldRegex = new RegExp(`\\*\\*${keyword}:?\\*\\*\\s*([\\s\\S]*?)(?=\\n\\*\\*(?:${MAJOR_SECTION_PATTERN}):?|\\n##\\s*\\w+|$)`, 'i');
                match = markdownContent.match(boldRegex);
                if (match && match[1]) return match[1].trim();
            }
            return '';
        };

        // 2. Extract Description
        // Description is text after title (if found) and before the first major section header
        let textAfterTitle = markdown;
        if (formData.title) {
            const titleIndex = markdown.toLowerCase().indexOf(formData.title.toLowerCase());
            if (titleIndex !== -1) {
                textAfterTitle = markdown.substring(titleIndex + formData.title.length);
            }
        }

        // H2: ## Description or ## Description:
        // Stop at any major section header (Ingredients, Method, Notes, Category, etc.),
        // not just any ## — this prevents "everything in description" when the model
        // is sloppy about outputting the other section headers.
        const descriptionSectionMatch = textAfterTitle.match(
            new RegExp(`^[ \\t]*##[ \\t]*Description:?[ \\t]*([\\s\\S]*?)(?=\\n##\\s*(?:${MAJOR_SECTION_PATTERN})|$)`, 'im')
        );

        if (descriptionSectionMatch && descriptionSectionMatch[1]) {
            formData.description = descriptionSectionMatch[1].trim().split('\n').filter(line => line.trim() !== '').slice(0, 5).join('\n').trim();
        } else {
            // Bolded **Description** or **Description:**
            const boldDescMatch = textAfterTitle.match(
                new RegExp(`\\*\\*Description:?\\*\\*[\\s\\n]*([\\s\\S]*?)(?=\\n\\*\\*(?:${MAJOR_SECTION_PATTERN}):?|\\n##\\s*\\w+|$)`, 'i')
            );
            if (boldDescMatch && boldDescMatch[1]) {
                formData.description = boldDescMatch[1].trim().split('\n').filter(line => line.trim() !== '').slice(0, 5).join('\n').trim();
            } else {
                // If "## Description" is not found, try to find "## Introduction" as a fallback.
                const introSectionMatch = textAfterTitle.match(
                    new RegExp(`^[ \\t]*##[ \\t]*Introduction:?[ \\t]*([\\s\\S]*?)(?=\\n##\\s*(?:${MAJOR_SECTION_PATTERN})|$)`, 'im')
                );
                if (introSectionMatch && introSectionMatch[1]) {
                    formData.description = introSectionMatch[1].trim().split('\n').filter((line: string) => line.trim() !== '').slice(0, 5).join('\n').trim();
                } else {
                    // Ultimate fallback: first paragraph after title
                    const paragraphs = textAfterTitle.split('\n\n').map(p => p.trim()).filter(p => p.length > 0 && !p.startsWith('##') && !p.startsWith('**'));
                    if (paragraphs.length > 0) {
                        formData.description = paragraphs[0].split('\n').filter((line: string) => line.trim() !== '').slice(0, 5).join('\n').trim();
                    }
                }
            }
        }

        // 3. Extract Ingredients
        const ingredientsContent = getSectionContent(['Ingredients'], markdown, formData.title || '');
        if (ingredientsContent) {
            formData.ingredients = ingredientsContent;
        }

        // 4. Extract Method/Instructions
        const methodContent = getSectionContent(['Method', 'Instructions', 'Directions'], markdown, formData.title || '');
        if (methodContent) {
            formData.method = methodContent;
        }

        // 5. Extract Notes (optional)
        const notesContent = getSectionContent(['Notes', 'Additional Notes', 'Tips'], markdown, formData.title || '');
        if (notesContent) {
            formData.notes = notesContent;
        }

        // 6. Extract Category (optional)
        // We deliberately do NOT default to 'Other' here — if the AI doesn't specify
        // a category (or the pasted text doesn't have one), leave it undefined so the
        // form's own default applies and the user can pick explicitly.
        const validCategories: string[] = ['Asian', 'Sri Lankan', 'Mexican', 'Latin American', 'Desserts', 'Drinks', 'Sauces', 'To Try'];
        const categoryContent = getSectionContent(['Category'], markdown, formData.title || '');
        if (categoryContent) {
            for (const cat of validCategories) {
                if (categoryContent.toLowerCase().includes(cat.toLowerCase())) {
                    formData.category = cat as any;
                    break;
                }
            }
        }

        // 6b. Fallback re-parse when the AI dumped everything into a single description block
        // (this is the bug we are hardening against). If description is suspiciously long
        // AND the structural sections are empty, try to split structurally.
        const descLen = formData.description?.trim().length || 0;
        const looksAllInDescription = descLen > 600 && !formData.ingredients && !formData.method;
        if (looksAllInDescription && formData.description) {
            const fallback = structuralFallbackParse(formData.description);
            if (fallback.ingredients) formData.ingredients = fallback.ingredients;
            if (fallback.method) formData.method = fallback.method;
            if (fallback.notes) {
                formData.notes = (formData.notes || '').trim()
                    ? `${formData.notes}\n\n${fallback.notes}`
                    : fallback.notes;
            }
            if (fallback.description && fallback.description !== formData.description) {
                formData.description = fallback.description;
            }
        }

        // 6c. Raw-text fallback for truncated AI output.
        // The AI frequently truncates its response for long pasted recipes — e.g. returning
        // only 5 of 25 ingredients and cutting off mid-sentence at "(or cold pressed...".
        // The original raw text the user pasted is the ground truth. For each section,
        // re-extract from the raw text using the same H2-based regex and prefer the raw
        // text version when it is substantially longer (clear truncation signal) OR when
        // the AI's version ends mid-sentence (another truncation signal).
        if (rawTextFallback && rawTextFallback.trim().length > 0) {
            const rawTitle = formData.title || '';
            const validCategories: string[] = ['Asian', 'Sri Lankan', 'Mexican', 'Latin American', 'Desserts', 'Drinks', 'Sauces', 'To Try'];

            // Helper: detect if extracted content ends mid-sentence (truncation signal).
            // Common cut-off patterns: trailing "(", ",", or conjunctions like "or", "and",
            // "the" with no following word.
            const endsMidSentence = (text: string): boolean => {
                if (!text) return false;
                const trimmed = text.trim();
                if (!trimmed) return false;
                const lines = trimmed.split('\n').map(l => l.trim()).filter(l => l.length > 0);
                if (lines.length === 0) return false;
                const lastLine = lines[lines.length - 1];
                if (/[(,]\s*$/.test(lastLine)) return true;
                if (/\b(or|and|the|a|an|with|to|of|in|for|into|but|or|so)\s*$/i.test(lastLine)) return true;
                return false;
            };

            // Helper: pick the best of (aiExtracted, rawExtracted) for a given section.
            // - If AI is empty, use raw (covers the original empty-section fallback case).
            // - If AI ends mid-sentence, use raw if raw is non-empty (truncation recovery).
            // - If raw is substantially longer (>= 1.3x), use raw (length-based truncation signal).
            // - Otherwise, keep AI's version (it's complete and similar length).
            const pickBest = (aiValue: string, rawValue: string, _sectionName: string): string => {
                if (!rawValue) return aiValue; // raw extraction failed, keep AI
                const aiLen = aiValue.trim().length;
                const rawLen = rawValue.trim().length;
                if (aiLen === 0) {
                    return rawValue;
                }
                const aiTruncated = endsMidSentence(aiValue);
                if (aiTruncated) {
                    return rawValue;
                }
                if (rawLen > aiLen * 1.3) {
                    return rawValue;
                }
                return aiValue;
            };

            // Ingredients
            const aiIngredients = formData.ingredients || '';
            const rawIngredients = getSectionContent(['Ingredients'], rawTextFallback, rawTitle);
            formData.ingredients = pickBest(aiIngredients, rawIngredients, 'Ingredients');

            // Method / Instructions / Directions
            const aiMethod = formData.method || '';
            const rawMethod = getSectionContent(['Method', 'Instructions', 'Directions'], rawTextFallback, rawTitle);
            formData.method = pickBest(aiMethod, rawMethod, 'Method');

            // Notes
            const aiNotes = formData.notes || '';
            const rawNotes = getSectionContent(['Notes', 'Additional Notes', 'Tips'], rawTextFallback, rawTitle);
            if (rawNotes) {
                const aiLen = aiNotes.trim().length;
                const rawLen = rawNotes.trim().length;
                if (aiLen === 0 || endsMidSentence(aiNotes) || rawLen > aiLen * 1.3) {
                    formData.notes = pickBest(aiNotes, rawNotes, 'Notes');
                }
            }

            // Category (only fill if missing — category is a single label, not bulk text,
            // so length comparison doesn't apply)
            if (!formData.category) {
                const rawCategory = getSectionContent(['Category'], rawTextFallback, rawTitle);
                if (rawCategory) {
                    for (const cat of validCategories) {
                        if (rawCategory.toLowerCase().includes(cat.toLowerCase())) {
                            formData.category = cat as any;
                            break;
                        }
                    }
                }
            }
        }

        // 7. Apply metadata if provided
        if (metadata) {
            if (metadata.persona) {
                // Add persona as a tag
                const currentTags = formData.tags || [];
                formData.tags = [...currentTags, metadata.persona];
            }
            if (metadata.dietaryTags && metadata.dietaryTags.length > 0) {
                const currentTags = formData.tags || [];
                formData.tags = [...currentTags, ...metadata.dietaryTags];
            }
        }

    } catch {
        // Return partially filled form data rather than failing completely
    }

    return formData;
}

/**
 * Extracts a summary of changes if the AI provides one.
 */
export function extractChangesSummary(aiResponse: string): string {
    // Try multiple patterns for changes summary
    const patterns = [
        /##\s*Changes Summary\s*\n([\s\S]*?)(?=\n##|$)/i,
        /##\s*Summary of Changes\s*\n([\s\S]*?)(?=\n##|$)/i,
        /##\s*Changes Made\s*\n([\s\S]*?)(?=\n##|$)/i,
        /\*\*Changes:\*\*([\s\S]*?)(?=\n\*\*|$)/i,
        /Summary:([\s\S]*?)(?=\n|$)/i
    ];

    for (const pattern of patterns) {
        const match = aiResponse.match(pattern);
        if (match && match[1]) {
            return match[1].trim();
        }
    }

    // Fallback: try to extract from modification context
    if (aiResponse.includes('egg') && aiResponse.includes('substitute')) {
        return "Replaced eggs with egg alternatives";
    }
    if (aiResponse.includes('sugar') && aiResponse.includes('substitute')) {
        return "Replaced sugar with low-carb alternatives";
    }
    if (aiResponse.includes('vegan') || aiResponse.includes('plant-based')) {
        return "Made recipe vegan-friendly";
    }
    if (aiResponse.includes('gluten-free')) {
        return "Made recipe gluten-free";
    }

    return "Recipe successfully modified";
}

/**
 * Parses the AI's response for a modified recipe.
 * Assumes AI returns the full recipe in markdown.
 */
export function parseModifiedRecipeResponse(aiResponse: string, originalFormData: RecipeFormData): RecipeFormData {
    // P1.2: the modification prompt doesn't request a structured appendix, but
    // extract one anyway if the model includes it — and CRITICALLY, invalidate
    // a stale original structured layer when the markdown it described changed.
    const { structured, markdown } = extractStructuredAppendix(aiResponse);

    // Use the existing robust parser on the appendix-stripped markdown
    const parsedData = parseMarkdownToRecipeForm(markdown);

    // This simple overlay is correct. The main handler is now responsible for description logic.
    const updatedFormData: RecipeFormData = {
        ...originalFormData,
        ...parsedData,
    };

    // Structured-layer freshness rule:
    // - fresh appendix in the response → adopt it
    // - markdown ingredients/method changed and no fresh appendix → drop the
    //   original layer (it now describes a different recipe; stale structured
    //   data would poison servings scaling / unit toggle)
    // - nothing changed → keep the original layer
    if (structured) {
        updatedFormData.structured = structured;
    } else if (originalFormData.structured) {
        const ingredientsChanged = parsedData.ingredients && parsedData.ingredients !== originalFormData.ingredients;
        const methodChanged = parsedData.method && parsedData.method !== originalFormData.method;
        if (ingredientsChanged || methodChanged) {
            updatedFormData.structured = null;
        }
    }

    return updatedFormData;
}

/**
 * Parses a structured JSON object (from Gemini's responseSchema output) into
 * Partial<RecipeFormData>. This is the primary path for the recipe creation
 * flows (paste import, studio, modification) when JSON mode is enabled —
 * it bypasses the regex-based markdown parser entirely.
 *
 * Robustness:
 * - Accepts any shape; missing fields default to empty/zero values.
 * - Trims string values and treats empty strings as missing.
 * - Validates `category` against the 8 narrow categories; unknown/null → undefined.
 * - Applies the same metadata rules as parseMarkdownToRecipeForm (persona/dietary tags).
 *
 * @param json The parsed JSON object (or null/undefined)
 * @param metadata Optional metadata (persona, dietary tags)
 * @returns Partial<RecipeFormData> ready for the form
 */
export function parseRecipeFromJson(
    json: unknown,
    metadata?: { persona?: string; dietaryTags?: string[] }
): Partial<RecipeFormData> {
    const validCategories = ['Asian', 'Sri Lankan', 'Mexican', 'Latin American', 'Desserts', 'Drinks', 'Sauces', 'To Try'];

    const formData: Partial<RecipeFormData> = {
        isFavorite: false,
        share: true,
        category: undefined,
        tags: [],
        notes: '',
        image_url: '',
        has_custom_image: false,
        title: '',
        description: '',
        ingredients: '',
        method: '',
    };

    if (!json || typeof json !== 'object') {
        return formData;
    }

    const obj = json as Record<string, unknown>;

    // Helper: extract a non-empty trimmed string from an unknown value
    const str = (v: unknown): string => {
        if (typeof v !== 'string') return '';
        const t = v.trim();
        return t;
    };

    const title = str(obj.title);
    if (title) formData.title = title;

    const description = str(obj.description);
    if (description) formData.description = description;

    const ingredients = str(obj.ingredients);
    if (ingredients) formData.ingredients = ingredients;

    const method = str(obj.method);
    if (method) formData.method = method;

    const notes = str(obj.notes);
    if (notes) formData.notes = notes;

    // Category: validate against the narrow list. If the model returned a string
    // that's not in the list (e.g. 'Other', 'Italian', null), leave it undefined
    // so the form's own default applies and the user can pick explicitly.
    const rawCategory = str(obj.category);
    if (rawCategory) {
        const match = validCategories.find(c => c.toLowerCase() === rawCategory.toLowerCase());
        if (match) formData.category = match as any;
    }

    // P1.2: structured layer (schema v2). Defensive validation — invalid shape
    // leaves formData.structured unset (legacy markdown-only recipe).
    const structured = parseStructuredRecipe(obj.structured);
    if (structured) {
        formData.structured = structured;
        // Markdown-always invariant: if the JSON response carried the structured
        // layer but no markdown strings, render them so legacy views keep working.
        if (!formData.ingredients && !formData.method) {
            const rendered = structuredToMarkdown(structured);
            if (!formData.ingredients) formData.ingredients = rendered.ingredients;
            if (!formData.method) formData.method = rendered.method;
        }
    }

    // Apply metadata (mirrors parseMarkdownToRecipeForm)
    if (metadata) {
        if (metadata.persona) {
            formData.tags = [...(formData.tags || []), metadata.persona];
        }
        if (metadata.dietaryTags && metadata.dietaryTags.length > 0) {
            formData.tags = [...(formData.tags || []), ...metadata.dietaryTags];
        }
    }

    return formData;
}

/**
 * Attempts to extract a JSON object from a string response. Useful as a safety net
 * when the model was asked for JSON but returned markdown-wrapped JSON (e.g.
 * ```json\n{...}\n```) — e.g. when JSON mode isn't supported by the active model.
 *
 * Returns the parsed object, or null if no valid JSON object could be extracted.
 */
export function tryParseJsonFromText(text: string): unknown | null {
    if (!text) return null;
    // 1. Direct parse
    try {
        const direct = JSON.parse(text);
        if (direct && typeof direct === 'object') return direct;
    } catch {
        // not direct JSON
    }
    // 2. Strip ```json ... ``` fences
    const fenced = text.match(/```(?:json)?\s*([\s\S]*?)```/i);
    if (fenced && fenced[1]) {
        try {
            const parsed = JSON.parse(fenced[1].trim());
            if (parsed && typeof parsed === 'object') return parsed;
        } catch {
            // not valid JSON inside fences
        }
    }
    // 3. Find the first {...} or [...] block
    const objectMatch = text.match(/\{[\s\S]*\}/);
    if (objectMatch) {
        try {
            const parsed = JSON.parse(objectMatch[0]);
            if (parsed && typeof parsed === 'object') return parsed;
        } catch {
            // not valid JSON
        }
    }
    return null;
}

// ── P1.2: structured recipe layer (schema v2) ────────────────────────────────

/**
 * Defensively coerce an unknown parsed value into a RecipeStructured.
 * Returns null unless the value looks like a usable structured recipe
 * (at minimum: a non-empty ingredients array with recognizable items).
 * Every field is validated/clamped — the model output is untrusted.
 */
export function parseStructuredRecipe(value: unknown): RecipeStructured | null {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
    const obj = value as Record<string, unknown>;

    const num = (v: unknown): number | null => {
        if (typeof v === 'number' && Number.isFinite(v) && v >= 0) return v;
        if (typeof v === 'string' && v.trim() !== '') {
            const n = Number(v);
            if (Number.isFinite(n) && n >= 0) return n;
        }
        return null;
    };
    const str = (v: unknown): string | null => {
        if (typeof v !== 'string') return null;
        const t = v.trim();
        return t !== '' ? t : null;
    };

    // servings
    let servings: RecipeStructured['servings'] = null;
    if (obj.servings && typeof obj.servings === 'object') {
        const s = obj.servings as Record<string, unknown>;
        const min = num(s.min);
        const max = num(s.max) ?? min;
        if (min !== null) servings = { min: Math.min(min, max ?? min), max: Math.max(min, max ?? min) };
    } else {
        const single = num(obj.servings);
        if (single !== null) servings = { min: single, max: single };
    }

    // times
    let times: RecipeStructured['times'] = null;
    if (obj.times && typeof obj.times === 'object') {
        const t = obj.times as Record<string, unknown>;
        const prep = num(t.prepMinutes);
        const cook = num(t.cookMinutes);
        if (prep !== null || cook !== null) times = { prepMinutes: prep, cookMinutes: cook };
    }

    // equipment
    const equipment: string[] = Array.isArray(obj.equipment)
        ? obj.equipment.map(str).filter((e): e is string => e !== null).slice(0, 30)
        : [];

    // ingredients — require at least 1 recognizable item, else the whole layer is junk
    const ingredients: StructuredIngredient[] = [];
    if (Array.isArray(obj.ingredients)) {
        for (const raw of obj.ingredients.slice(0, 100)) {
            if (!raw || typeof raw !== 'object') continue;
            const ing = raw as Record<string, unknown>;
            const item = str(ing.item) ?? str(ing.name) ?? str(ing.ingredient);
            if (!item) continue;
            ingredients.push({
                qty: num(ing.qty),
                unit: str(ing.unit),
                imperial: str(ing.imperial),
                item,
                note: str(ing.note),
                group: str(ing.group),
            });
        }
    }
    if (ingredients.length === 0) return null;

    // steps
    const steps: StructuredStep[] = [];
    if (Array.isArray(obj.steps)) {
        for (const raw of obj.steps.slice(0, 100)) {
            if (typeof raw === 'string') {
                const text = str(raw);
                if (text) steps.push({ text, timingMinutes: null });
                continue;
            }
            if (!raw || typeof raw !== 'object') continue;
            const st = raw as Record<string, unknown>;
            const text = str(st.text) ?? str(st.step) ?? str(st.instruction);
            if (!text) continue;
            steps.push({ text, timingMinutes: num(st.timingMinutes) ?? num(st.minutes) });
        }
    }

    return { servings, times, equipment, ingredients, steps };
}

/**
 * Extract the structured appendix from a model response and return the
 * markdown with the appendix stripped.
 *
 * Looks for a fenced ```recipe-data (or ```json) block whose payload parses
 * and validates as a structured recipe. The LAST matching block wins (the
 * appendix is instructed to come after the markdown). When nothing valid is
 * found, the markdown is returned unchanged.
 */
export function extractStructuredAppendix(text: string): { structured: RecipeStructured | null; markdown: string } {
    if (!text) return { structured: null, markdown: text };
    const fenceRe = /```(?:recipe-data|json)\s*([\s\S]*?)```/gi;
    let match: RegExpExecArray | null;
    let structured: RecipeStructured | null = null;
    let span: { start: number; end: number } | null = null;
    while ((match = fenceRe.exec(text)) !== null) {
        try {
            const parsed = JSON.parse(match[1].trim());
            const candidate = parseStructuredRecipe(parsed);
            if (candidate) {
                structured = candidate;
                span = { start: match.index, end: match.index + match[0].length };
            }
        } catch {
            // not valid JSON — keep scanning
        }
    }
    if (!structured || !span) return { structured: null, markdown: text };
    // Strip the appendix block (plus a trailing blank line so notes stay clean)
    const markdown = (text.slice(0, span.start) + text.slice(span.end)).replace(/\n{3,}$/, '\n').trimEnd();
    return { structured, markdown };
}

/**
 * Render the structured layer back to markdown section strings. Safety net for
 * JSON-only responses (legacy responseFormat path) where the markdown string
 * fields are missing — guarantees the "markdown is always populated" invariant.
 */
export function structuredToMarkdown(s: RecipeStructured): { ingredients: string; method: string } {
    const lines: string[] = [];
    let currentGroup: string | null = null;
    for (const ing of s.ingredients) {
        if (ing.group && ing.group !== currentGroup) {
            currentGroup = ing.group;
            lines.push(`**${currentGroup}:**`);
        }
        const qtyUnit = [ing.qty !== null ? String(ing.qty) : null, ing.unit]
            .filter(Boolean).join('');
        const imperial = ing.imperial ? ` (${ing.imperial})` : '';
        const note = ing.note ? `, ${ing.note}` : '';
        const measure = qtyUnit ? `${qtyUnit}${imperial} ` : '';
        lines.push(`- ${measure}${ing.item}${note}`);
    }
    const method = s.steps
        .map((st, i) => `${i + 1}. ${st.text}${st.timingMinutes !== null ? ` (${st.timingMinutes} minutes)` : ''}`)
        .join('\n');
    return { ingredients: lines.join('\n'), method };
}
